/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { DisconnectReason } from "livekit-client";

import { callTraceJournal } from "../telemetry";
import type { RecoveryCycleSummaryPayload } from "../telemetry";
import type {
  CallRecoveryCancellationCode,
  CallRecoveryMode,
  CallRecoverySignal,
  CallRecoveryStage,
  CallRecoveryState,
} from "./CallRecoveryTypes";

type RecoveryFinalOutcome = "connected" | "terminal" | "cancelled";

export interface CallRecoveryTelemetrySummary
  extends RecoveryCycleSummaryPayload {
  readonly callSessionId: string;
  readonly cycleId: string;
  readonly connectionEpoch: number;
  readonly mode: CallRecoveryMode;
  readonly finalOutcome: RecoveryFinalOutcome;
  readonly durationMs: number;
  readonly presentationFreezeDurationMs: number;
  readonly maxAttempt: number;
  readonly signalCount: number;
  readonly signalCounts: Readonly<Record<string, number>>;
  readonly livekitDisconnectReasons: string[];
  readonly stages: CallRecoveryStage[];
  readonly holdingDurationsMs: Readonly<Record<string, number>>;
  readonly longestHoldingStage: string | null;
  readonly totalBackoffMs: number;
  readonly maxBackoffMs: number;
  readonly lastNormalizedCode: string | null;
  readonly lastStableSignature: string | null;
  readonly terminalCode: string | null;
  readonly cancellationReason: CallRecoveryCancellationCode | null;
}

interface CycleAggregate {
  readonly callSessionId: string;
  readonly cycleId: string;
  readonly connectionEpoch: number;
  readonly mode: CallRecoveryMode;
  readonly startedAt: number;
  maxAttempt: number;
  observedSignalCount: number;
  readonly signalCounts: Record<string, number>;
  readonly livekitDisconnectReasons: Set<string>;
  readonly stages: CallRecoveryStage[];
  readonly holdingDurationsMs: Record<string, number>;
  currentHoldingStage: string | null;
  lastObservedAt: number;
  totalBackoffMs: number;
  maxBackoffMs: number;
  lastNormalizedCode: string | null;
  lastStableSignature: string | null;
  finalized: boolean;
}

export interface CallRecoveryTelemetryOptions {
  readonly now?: () => number;
  readonly recordTrace?: typeof callTraceJournal.record;
  readonly reportIncident?: (incident: {
    readonly classification: "handled";
    readonly reason: string;
    readonly screen: string;
    readonly trigger: string;
    readonly dedupeKey: string;
    readonly details: Record<string, unknown>;
  }) => void;
  /**
   * Registry находится выше React lifecycle. Поэтому повторная подписка,
   * StrictMode и remount не отправляют второй incident того же cycle.
   */
  readonly reportedCycleKeys?: Set<string>;
}

const reportedRecoveryCycleKeys = new Set<string>();

function rememberReportedCycle(registry: Set<string>, key: string): boolean {
  if (registry.has(key)) return false;
  registry.add(key);
  return true;
}

function describeDisconnectReason(signal: CallRecoverySignal): string | null {
  if (signal.type !== "livekit_disconnected") return null;
  if (signal.reason === undefined) return "unknown";
  return DisconnectReason[signal.reason] ?? String(signal.reason);
}

function getHoldingStage(state: CallRecoveryState): string | null {
  if (state.phase === "waiting_for_network") return "waiting_for_network";
  if (state.nextDelayMs !== null) {
    return `backoff:${state.stage ?? "unknown"}`;
  }
  return state.stage;
}

function isFinalPhase(
  phase: CallRecoveryState["phase"],
): phase is RecoveryFinalOutcome {
  return phase === "connected" || phase === "terminal" || phase === "cancelled";
}

/**
 * Диагностический агрегатор читает только state$ coordinator и не принимает
 * решений о recovery. Он сохраняет переходы, которые coordinator очищает в
 * финальном emission, и формирует один безопасный итог cycle.
 */
export class CallRecoveryTelemetryObserver {
  private readonly now: () => number;
  private readonly recordTrace: typeof callTraceJournal.record;
  private readonly reportIncident: NonNullable<
    CallRecoveryTelemetryOptions["reportIncident"]
  >;
  private readonly reportedCycleKeys: Set<string>;
  private aggregate: CycleAggregate | null = null;

  public constructor(options: CallRecoveryTelemetryOptions = {}) {
    this.now = options.now ?? Date.now;
    this.recordTrace =
      options.recordTrace ??
      ((event): string => callTraceJournal.record(event));
    this.reportIncident = options.reportIncident ?? ((): void => undefined);
    this.reportedCycleKeys =
      options.reportedCycleKeys ?? reportedRecoveryCycleKeys;
  }

  public observe(state: CallRecoveryState): void {
    if (state.cycleId === null || state.mode === null) return;
    const now = this.now();
    if (this.aggregate?.cycleId !== state.cycleId) {
      const lifecycleKey = `${state.callSessionId}|${state.cycleId}`;
      this.aggregate = {
        callSessionId: state.callSessionId,
        cycleId: state.cycleId,
        connectionEpoch: state.connectionEpoch,
        mode: state.mode,
        startedAt: state.startedAt ?? now,
        maxAttempt: 0,
        observedSignalCount: 0,
        signalCounts: {},
        livekitDisconnectReasons: new Set(),
        stages: [],
        holdingDurationsMs: {},
        currentHoldingStage: null,
        lastObservedAt: now,
        totalBackoffMs: 0,
        maxBackoffMs: 0,
        lastNormalizedCode: null,
        lastStableSignature: null,
        finalized: false,
      };
      if (
        rememberReportedCycle(this.reportedCycleKeys, `started|${lifecycleKey}`)
      ) {
        this.recordTrace({
          eventType: "call.recovery_cycle_started",
          stage: "recovery",
          severity: "warn",
          outcome: "started",
          reasonCode: state.mode,
          payload: {
            callSessionId: state.callSessionId,
            cycleId: state.cycleId,
            connectionEpoch: state.connectionEpoch,
            mode: state.mode,
          },
        });
      }
    }

    const aggregate = this.aggregate;
    if (aggregate.finalized) return;
    this.accumulateHoldingDuration(aggregate, now);
    aggregate.currentHoldingStage = getHoldingStage(state);
    aggregate.lastObservedAt = now;
    aggregate.maxAttempt = Math.max(aggregate.maxAttempt, state.attempt);
    aggregate.maxBackoffMs = Math.max(
      aggregate.maxBackoffMs,
      state.nextDelayMs ?? 0,
    );

    if (
      state.stage !== null &&
      aggregate.stages[aggregate.stages.length - 1] !== state.stage
    ) {
      aggregate.stages.push(state.stage);
    }
    if (state.lastError !== null) {
      aggregate.lastNormalizedCode = state.lastError.normalizedCode;
      aggregate.lastStableSignature = state.lastError.stableSignature;
    }
    for (
      let index = aggregate.observedSignalCount;
      index < state.signals.length;
      index += 1
    ) {
      const signal = state.signals[index];
      aggregate.signalCounts[signal.type] =
        (aggregate.signalCounts[signal.type] ?? 0) + 1;
      const disconnectReason = describeDisconnectReason(signal);
      if (disconnectReason !== null) {
        aggregate.livekitDisconnectReasons.add(disconnectReason);
      }
    }
    aggregate.observedSignalCount = Math.max(
      aggregate.observedSignalCount,
      state.signals.length,
    );

    if (isFinalPhase(state.phase)) {
      this.finish(aggregate, state, now);
    }
  }

  private accumulateHoldingDuration(
    aggregate: CycleAggregate,
    now: number,
  ): void {
    if (aggregate.currentHoldingStage === null) return;
    const elapsed = Math.max(0, now - aggregate.lastObservedAt);
    aggregate.holdingDurationsMs[aggregate.currentHoldingStage] =
      (aggregate.holdingDurationsMs[aggregate.currentHoldingStage] ?? 0) +
      elapsed;
  }

  private finish(
    aggregate: CycleAggregate,
    state: CallRecoveryState,
    now: number,
  ): void {
    aggregate.finalized = true;
    const terminalError = state.terminalOutcome?.error ?? null;
    const holdingEntries = Object.entries(aggregate.holdingDurationsMs);
    aggregate.totalBackoffMs = holdingEntries
      .filter(([stage]) => stage.startsWith("backoff:"))
      .reduce((total, [, duration]) => total + duration, 0);
    const longestHoldingStage =
      holdingEntries.length === 0
        ? null
        : holdingEntries.reduce((longest, current) =>
            current[1] > longest[1] ? current : longest,
          )[0];
    const summary: CallRecoveryTelemetrySummary = {
      callSessionId: aggregate.callSessionId,
      cycleId: aggregate.cycleId,
      connectionEpoch: aggregate.connectionEpoch,
      mode: aggregate.mode,
      finalOutcome: state.phase as RecoveryFinalOutcome,
      durationMs: Math.max(0, now - aggregate.startedAt),
      presentationFreezeDurationMs: Math.max(0, now - aggregate.startedAt),
      maxAttempt: aggregate.maxAttempt,
      signalCount: aggregate.observedSignalCount,
      signalCounts: { ...aggregate.signalCounts },
      livekitDisconnectReasons: [
        ...aggregate.livekitDisconnectReasons.values(),
      ],
      stages: [...aggregate.stages],
      holdingDurationsMs: { ...aggregate.holdingDurationsMs },
      longestHoldingStage,
      totalBackoffMs: aggregate.totalBackoffMs,
      maxBackoffMs: aggregate.maxBackoffMs,
      lastNormalizedCode:
        terminalError?.normalizedCode ?? aggregate.lastNormalizedCode,
      lastStableSignature:
        terminalError?.stableSignature ?? aggregate.lastStableSignature,
      terminalCode: terminalError?.code ?? null,
      cancellationReason: state.cancellationReason ?? null,
    };
    const reasonCode =
      summary.terminalCode ??
      summary.cancellationReason ??
      summary.finalOutcome;

    const reportKey = `${summary.callSessionId}|${summary.cycleId}`;
    if (
      !rememberReportedCycle(this.reportedCycleKeys, `finished|${reportKey}`)
    ) {
      return;
    }
    this.recordTrace({
      eventType: "call.recovery_cycle_finished",
      stage: "recovery",
      severity: summary.finalOutcome === "terminal" ? "error" : "info",
      outcome:
        summary.finalOutcome === "connected"
          ? "recovered"
          : summary.finalOutcome === "cancelled"
            ? "cancelled"
            : "failed",
      reasonCode,
      durationMs: summary.durationMs,
      payload: summary,
    });

    if (!this.shouldReportRageshake(summary)) return;
    const incident = {
      classification: "handled",
      reason: "call_recovery_cycle",
      screen: "ActiveCall",
      trigger: `Hard recovery завершился с outcome ${summary.finalOutcome}.`,
      dedupeKey: reportKey,
      details: summary,
    } as const;
    this.reportIncident(incident);
  }

  /**
   * Обычный initial connect не является инцидентом. Для него достаточно trace.
   * Automatic rejoin уже означает срыв стабильного пути; проблемный initial
   * определяется наблюдавшимся retry/wait/error/signal либо terminal outcome.
   */
  private shouldReportRageshake(
    summary: CallRecoveryTelemetrySummary,
  ): boolean {
    if (summary.mode === "automatic_rejoin") return true;
    if (summary.finalOutcome === "terminal") return true;
    return (
      summary.maxAttempt > 1 ||
      summary.signalCount > 0 ||
      summary.lastStableSignature !== null ||
      summary.maxBackoffMs > 0 ||
      Object.hasOwn(summary.holdingDurationsMs, "waiting_for_network")
    );
  }
}
